import React, { useState, useEffect } from 'react';
import Login from './components/Login';
import StudioSignIn from './components/StudioSignIn';
import CustomerApp from './layouts/CustomerApp';
import EcapChatPage from './pages/EcapChatPage';
import KrapfCharterMail from './pages/KrapfCharterMail';
import DemoPage from './pages/DemoPage';
import DriverApp from './layouts/DriverApp';
import OpsApp from './layouts/OpsApp';
import StudioApp from './layouts/StudioApp';
import CharterDispatchApp from './charter/CharterDispatchApp';
import CharterConfigPanel from './charter/CharterConfigPanel';
import AdminPage from './pages/AdminPage';
import { getCurrentSession, signOut, getCurrentEcapSession, signOutEcap } from './services/authService';
import { setStudioAuthSource } from './services/ecapApi';
import { resolveProduct, canonicalPath, isLandingRoute } from './routing/resolveProduct';
import {
  identityFromClaims, canOpenRoute, landingPath, companyStudioAccess, STUDIO_PATH,
} from './access/permissions';
import './App.css';

// Human names for the surfaces, so a wrong-account message can say WHICH app was refused.
const SURFACE_LABEL = {
  ops: 'the Operations dashboard',
  dispatch: 'the Dispatch board',
  driver: 'the Driver app',
  chat: 'the customer chat',
  ecapchat: 'the customer chat',
  config: 'the charter configuration',
};

// '/' and unknown paths alias to the default customer chat, and /portal is a retired launcher: none of them
// is a destination a signed-in user asked for, so all of them send the user to their landing page.
function isDefaultLanding(rawPath) {
  const cfg = resolveProduct(rawPath);
  return rawPath === '/' || rawPath === '' || cfg === null || isLandingRoute(cfg);
}

function getUserName(userClaims) {
  const firstName = userClaims.given_name || userClaims['custom:first_name'] || userClaims.name || '';
  const lastName = userClaims.family_name || userClaims['custom:last_name'] || '';
  return (firstName + ' ' + lastName).trim()
    || userClaims['cognito:username']
    || userClaims.email
    || userClaims.sub
    || '';
}

// Dev mode (/admin URL or 5-tap on login logo): a mock session in the picked Cognito GROUP, so a
// developer can preview any surface — incl. Peoria (`peoria_operations`) — without a real login.
function createMockSession(group) {
  return {
    idToken: 'mock-token',
    accessToken: 'mock-token',
    refreshToken: 'mock-token',
    user: {
      sub: 'dev-user-001',
      given_name: 'Dev',
      family_name: group.charAt(0).toUpperCase() + group.slice(1),
      email: `dev-${group}@movorra.ai`,
      'cognito:username': `dev-${group}`,
      'cognito:groups': [group],
    },
  };
}

// Render the component for a resolved product/surface config. A chat surface always renders as the
// anonymous webchat (identity never shown/sent), branded + run-targeted by the path config.
function renderSurface(cfg, { authSession, userName, onLogout }) {
  switch (cfg.surface) {
    case 'driver':
      return <DriverApp authSession={authSession} userName={userName} onLogout={onLogout} />;
    case 'ops':
      return <OpsApp isAdmin={!!cfg.isAdmin} authSession={authSession} userName={userName} onLogout={onLogout} />;
    case 'dispatch':
      return <CharterDispatchApp authSession={authSession} userName={userName} onLogout={onLogout} />;
    case 'config':
      return <CharterConfigPanel authSession={authSession} />;
    case 'ecapchat':
      // A customer chat that runs on the ECAP flow engine (Studio flow) instead of the agents service.
      return <EcapChatPage cfg={cfg} onLogout={onLogout} />;
    case 'chartermail':
      // Operator-facing charter inquiry inbox: reads Outlook over Graph and runs a Studio flow per
      // email thread. Staff-only, so it renders inside the authenticated surface switch.
      return <KrapfCharterMail cfg={cfg} userName={userName} onLogout={onLogout} />;
    case 'demo':
      // A Deploy-Demo link. Normally rendered by the isPublicChat branch above (a demo is never gated);
      // this case only covers the /admin dev-mode path, so the switch stays total.
      return <DemoPage />;
    case 'chat':
    default:
      return (
        <CustomerApp
          anonymous
          authSession={null}
          setAuthSession={() => {}}
          userName=""
          onLogout={onLogout}
          brand={cfg.brand}
          runConfig={{ teamId: cfg.teamId, agentId: cfg.agentId }}
        />
      );
  }
}

function App() {
  const [authSession, setAuthSession] = useState(null);
  const [ecapSession, setEcapSession] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [devMode, setDevMode] = useState(false);
  const [devGroup, setDevGroup] = useState(null);

  const rawPath = window.location.pathname;
  const host = window.location.hostname.toLowerCase();
  // The /admin path enables a role-picker DEV session (createMockSession fabricates a mock token + groups).
  // It must never be reachable on a production host — otherwise any visitor to www.movorra.ai/admin gets the
  // internal operator/Studio console shell rendered. Gate it to dev hosts / localhost only (the deployed bundle
  // is NODE_ENV=production on BOTH dev.* and prod, so the host — not NODE_ENV — is the discriminator, matching
  // the chat-login gate below).
  const isDevHost = host.startsWith('dev.') || host.includes('.dev.') || host === 'localhost' || host === '127.0.0.1';
  // SECURITY (r3-demo): the /admin mock-session role picker is LOCALHOST-ONLY now. On the deployed dev
  // site it let any unauthenticated visitor render the internal ops/Studio console shells (the reported
  // "admin access without login" issue) — the API rejects the mock token so data didn't load, but the
  // internal UI must not be reachable either. Deployed dev/prod staff use their real Cognito logins.
  const isLocalHost = host === 'localhost' || host === '127.0.0.1';
  const isAdminPath = rawPath === '/admin' && isLocalHost;
  // Movorra Studio (/studio). A Movorra super-admin uses their single ride-share login here (PLATFORM-SSO-1);
  // Studio-only accounts (company Studio users) sign in through the same form, which falls back to the Studio pool.
  const isStudioPath = rawPath === '/studio' || rawPath.startsWith('/studio/');
  // The path decides product + surface. Unknown paths default to the R3 customer chat (today's
  // root-shows-chat behavior). Old paths (/chat, /dispatchops, /charterops…) are aliased inside
  // resolveProduct so existing bookmarks keep working.
  const cfg = resolveProduct(rawPath) || resolveProduct('/r3chat');
  const isChatSurface = cfg.surface === 'chat' || cfg.surface === 'ecapchat';

  // The customer webchat is always anonymous. In prod it's PUBLIC; in DEVL a Cognito login gates the
  // ENV in front of it. Explicit env var wins; otherwise infer from the host (dev.* gates, prod public).
  const chatLoginFlag = process.env.REACT_APP_REQUIRE_CHAT_LOGIN;
  const REQUIRE_CHAT_LOGIN =
    chatLoginFlag === 'true' ? true
    : chatLoginFlag === 'false' ? false
    : host.startsWith('dev.') || host.includes('.dev.');
  // A Deploy-Demo link is ALWAYS public — being openable by whoever you sent it to is the entire point.
  // OR'd in ahead of REQUIRE_CHAT_LOGIN so the dev.* host inference can never put a Cognito wall in front
  // of a demo you're presenting. The demo's own token is what authorizes it, and the backend revokes it.
  // `alwaysPublic` on the route wins over the environment's chat-login wall. It is what makes a
  // Deploy-Demo link openable by whoever you sent it to, and it is now also set on /r3ride so the R3
  // booking page is reachable with no account on EVERY host, devl included. /r3chat is public the same way
  // (R3-CHAT-CARD-1) — but only when asked for by name: '/' and unknown paths also resolve to /r3chat, and on a
  // login-gated host they must keep showing the login, not turn into the chat.
  const isPublicChat = (!!cfg.alwaysPublic && !isDefaultLanding(rawPath)) || (isChatSurface && !REQUIRE_CHAT_LOGIN);

  useEffect(() => {
    if (isAdminPath) {
      setDevMode(true);
      setAuthLoading(false);
      return;
    }
    if (isStudioPath) {
      // Both sessions: the ride-share one opens Studio only for a super-admin; the Studio-pool one as before.
      Promise.allSettled([getCurrentSession(), getCurrentEcapSession()]).then(([ride, studio]) => {
        if (ride.status === 'fulfilled') setAuthSession(ride.value);
        if (studio.status === 'fulfilled') setEcapSession(studio.value);
      }).finally(() => setAuthLoading(false));
      return;
    }
    if (isPublicChat) {
      setAuthLoading(false);   // skip Cognito — render the anonymous webchat below
      return;
    }
    getCurrentSession()
      .then((session) => setAuthSession(session))
      .catch(() => {})
      .finally(() => setAuthLoading(false));
  }, [isAdminPath, isStudioPath, isPublicChat]);

  // After a login, prefer the surface the user was actually trying to open; fall back to their own home.
  //
  // LOGIN-DEST-1: this used to redirect to homePathForGroups() unconditionally, ignoring the current URL.
  // That broke every deep link into an authenticated surface — most visibly "Open Application" from the
  // company portal: clicking Operations Dashboard (/r3ops) and signing in bounced an `operations` account
  // to /r3dispatch, and an account carrying any peoria_* group all the way to /peoriaops (peoria is
  // matched first in homePathForGroups), which reads as "it opened all the Peoria stuff".
  //
  // The canAccess() guard preserves the original reason for the unconditional redirect: the URL is sticky
  // across logout -> login, so a path this account CANNOT open must never trap it. A path it can open is
  // exactly where it asked to go.
  const handleLogin = (session) => {
    setAuthSession(session);
    const identity = identityFromClaims(session.user || {}, 'rideshare');
    const raw = window.location.pathname;
    const here = canonicalPath(raw);
    // The default landing ('/', an unknown path, or the retired /portal) is NOT a destination: '/' aliases to
    // /r3chat, which R3 staff can open (CHATBOUNCE-1), so treating it as "where they asked to go" would strand
    // a dispatcher on the customer chat instead of their board.
    const hereCfg = resolveProduct(here);
    const dest = !isDefaultLanding(raw) && canOpenRoute(identity, hereCfg)
      ? here
      : landingPath(identity);
    if (here !== dest) window.location.href = dest;
  };

  // Sign-out ends BOTH pool sessions, so one login never leaves the other signed in behind it.
  const handleLogout = () => {
    if (devMode) {
      setDevGroup(null);
      return;
    }
    signOut();
    signOutEcap();
    // Reset to a clean root so the next login never starts on a stale product URL.
    window.location.href = '/';
  };

  const handleStudioLogout = () => {
    signOut();
    signOutEcap();
    window.location.href = '/studio';
  };

  const redirecting = (
    <div className="min-h-screen w-full flex items-center justify-center text-sm text-slate-500">
      Redirecting…
    </div>
  );

  // Authenticated but this account has no surface it may open (e.g. a pool login with no product group).
  // Deny cleanly instead of bouncing forever.
  // WRONG-ACCOUNT-1: what a signed-in user sees when they open a surface their account cannot.
  //
  // This used to silently redirect them to their own home whenever that home was reachable, which made
  // the app look broken: opening /r3driver while signed in as an admin just snapped back to /r3ops with
  // no explanation and no login prompt — so there was no way to sign in AS a driver without manually
  // clearing the session. Say which account is signed in, that it cannot open this app, and always offer
  // a way out.
  const noAccessFor = (label, homePath, canGoHome) => (
    <div className="min-h-screen w-full flex flex-col items-center justify-center gap-4 px-4 text-center">
      <div className="max-w-sm">
        <p className="text-sm text-slate-800 font-medium mb-1">
          {label ? `This account can’t open ${label}.` : 'This account doesn’t have access to any workspace.'}
        </p>
        <p className="text-[13px] text-slate-500 leading-relaxed">
          Signed in as <span className="font-medium text-slate-700">{getUserName(authSession?.user || {})}</span>.
          Sign out to use a different login.
        </p>
      </div>
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={handleLogout}
          className="px-4 py-2 rounded-lg bg-slate-900 text-white text-sm hover:bg-slate-700 transition-colors"
        >
          Sign out
        </button>
        {canGoHome && (
          <a
            href={homePath}
            className="px-4 py-2 rounded-lg border border-slate-300 text-slate-700 text-sm hover:bg-slate-50 transition-colors"
          >
            Go to my dashboard
          </a>
        )}
      </div>
    </div>
  );

  // Dev mode — render the picked group's HOME surface (so a dev can preview any product/surface).
  if (devMode) {
    if (devGroup) {
      // 'ecap' is a dev shortcut: a mock session opened straight on the Studio Automation page.
      if (devGroup === 'ecap') {
        const mock = createMockSession('operations');
        return <StudioApp authSession={mock} userName={getUserName(mock.user)}
                       onLogout={handleLogout} initialPage="ops-flows" />;
      }
      const mockSession = createMockSession(devGroup);
      const userName = getUserName(mockSession.user);
      const identity = identityFromClaims(mockSession.user, 'rideshare');
      const homeCfg = resolveProduct(landingPath(identity)) || cfg;
      return renderSurface(homeCfg, { authSession: mockSession, userName, onLogout: handleLogout });
    }
    return <AdminPage onSelectRole={setDevGroup} />;
  }

  if (authLoading) {
    return (
      <div className="min-h-screen w-full flex items-center justify-center text-sm text-slate-500">
        Loading…
      </div>
    );
  }

  // Movorra Studio (/studio).
  //  1. A ride-share login carrying movorra-admin → Studio on that same login (one login for everything).
  //  2. A Studio-pool session → Studio as before.
  //  2b. A company administrator with Studio access (r3_admin → R3) → Studio limited to that company's screens.
  //  3. Any other company login → their own app (dispatcher → /r3dispatch, driver → /r3driver): /studio is the one
  //     sign-in link. With no app at all, say this account can't open anything here.
  //  4. Nobody signed in → StudioSignIn: one form, company login first, Studio login as fallback (MOVORRA-STAFF-1).
  if (isStudioPath) {
    const rideIdentity = authSession ? identityFromClaims(authSession.user || {}, 'rideshare') : null;
    if (rideIdentity && rideIdentity.isSuperAdmin) {
      setStudioAuthSource('rideshare');
      return (
        <StudioApp
          authSession={authSession}
          userName={getUserName(authSession.user || {})}
          onLogout={handleStudioLogout}
          singleLogin
        />
      );
    }
    const companyStudio = rideIdentity ? companyStudioAccess(rideIdentity) : null;
    if (companyStudio) {
      setStudioAuthSource('rideshare');
      return (
        <StudioApp
          authSession={authSession}
          userName={getUserName(authSession.user || {})}
          onLogout={handleStudioLogout}
          singleLogin
          allowedScreens={companyStudio.screens}
          initialPage={companyStudio.screens[0]}
        />
      );
    }
    if (ecapSession) {
      setStudioAuthSource('studio');
      return (
        <StudioApp
          authSession={ecapSession}
          userName={getUserName(ecapSession.user || {})}
          onLogout={handleStudioLogout}
        />
      );
    }
    if (authSession) {
      const home = landingPath(rideIdentity);
      if (home !== STUDIO_PATH && canOpenRoute(rideIdentity, resolveProduct(home))) {
        window.location.href = home; // a dispatcher, driver, … signed in at the one link → their own app
        return redirecting;
      }
      const useDifferentAccount = () => {
        signOut();
        signOutEcap();
        setAuthSession(null);
        setEcapSession(null);
      };
      return (
        <div className="min-h-screen w-full flex flex-col items-center justify-center gap-4 px-4 text-center">
          <div className="max-w-sm">
            <p className="text-sm text-slate-800 font-medium mb-1">This account can’t open Movorra Studio.</p>
            <p className="text-[13px] text-slate-500 leading-relaxed">
              Signed in as <span className="font-medium text-slate-700">{getUserName(authSession.user || {})}</span>.
              Studio needs a Movorra administrator login or a Studio account.
            </p>
          </div>
          <div className="flex flex-wrap items-center justify-center gap-2">
            <button type="button" onClick={handleStudioLogout}
              className="px-4 py-2 rounded-lg bg-slate-900 text-white text-sm hover:bg-slate-700 transition-colors">
              Sign out
            </button>
            <button type="button" onClick={useDifferentAccount}
              className="px-4 py-2 rounded-lg border border-slate-300 text-slate-700 text-sm hover:bg-slate-50 transition-colors">
              Use a different account
            </button>
            {home !== STUDIO_PATH && (
              <a href={home}
                className="px-4 py-2 rounded-lg border border-slate-300 text-slate-700 text-sm hover:bg-slate-50 transition-colors">
                Go to my dashboard
              </a>
            )}
          </div>
        </div>
      );
    }
    return <StudioSignIn onCompanyLogin={setAuthSession} onStudioLogin={setEcapSession} />;
  }

  // Anonymous customer webchat (no login) — brand + run target from the path.
  if (isPublicChat) {
    if (cfg.surface === 'demo') {
      return <DemoPage />;
    }
    if (cfg.surface === 'ecapchat') {
      return <EcapChatPage cfg={cfg} onLogout={null} />;
    }
    if (cfg.surface === 'chartermail') {
      // The open sandbox: a fake mailbox with a real intake behind it. config/krapf.js only marks the
      // application public while the mock provider is in use, so this branch can never serve a page
      // pointed at somebody's real Outlook.
      return <KrapfCharterMail cfg={cfg} userName="" onLogout={null} anonymous />;
    }
    return (
      <CustomerApp
        anonymous
        authSession={null}
        setAuthSession={() => {}}
        userName=""
        onLogout={null}
        brand={cfg.brand}
        runConfig={{ teamId: cfg.teamId, agentId: cfg.agentId }}
      />
    );
  }

  if (!authSession) {
    return <Login onLogin={handleLogin} onDevMode={() => setDevMode(true)} />;
  }

  const userClaims = authSession.user || {};
  // Built from the RIDE-SHARE token only — the token every app below sends to movorra-api.
  const identity = identityFromClaims(userClaims, 'rideshare');
  const userName = getUserName(userClaims);

  // Single entry point: a user on the DEFAULT landing ('/', an unknown path, or the retired /portal) goes to
  // their landing page — /studio for a Movorra super-admin, otherwise their company home (see
  // access/permissions.js). Explicit application URLs are NOT redirected; they are gated below.
  const home = landingPath(identity);
  if (isDefaultLanding(rawPath) && canonicalPath(rawPath) !== home) {
    window.location.href = home;   // full reload; the Cognito session persists in localStorage → no loop
    return redirecting;
  }

  // STRICT per-surface access: EVERY surface — chat included — is gated by its requirements; there is no chat
  // bypass. This keeps R3<->Peoria isolation and per-surface confinement for company users. A Movorra
  // super-admin may additionally open every company application (the APIs still enforce their own checks).
  if (!canOpenRoute(identity, cfg)) {
    // WRONG-ACCOUNT-1: an EXPLICIT request for a surface this account cannot open is answered, not bounced —
    // say which account is signed in and offer Sign out, so testing a second role stays possible.
    const homeAccessible = home === STUDIO_PATH || canOpenRoute(identity, resolveProduct(home));
    return noAccessFor(SURFACE_LABEL[cfg.surface] || 'this application', home,
      homeAccessible && canonicalPath(rawPath) !== home);
  }
  return renderSurface(cfg, { authSession, userName, onLogout: handleLogout });
}

export default App;
